<template>
  <view-wrapper
    :showBackButton="false"
    :name="wrapperName"
    :loading="loading"
    :loading-text="loadingText"
  >
    <FeatureZone features="quickPrintEnabled">
      <g-button
        class="flex items-center mb-4 ml-auto primary"
        @click="openQuickPrintModal"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
          />
        </svg>
        Print
      </g-button>
    </FeatureZone>
    <filter-and-sort-box
      :data="orders"
      :headers="headers"
      :use-search="true"
      @searchQuery="onSearch"
      defaultSortKey="created"
      :defaultSortOrder="1"
    >
      <template v-slot:d="{ d }">
        <td
          class="relative py-1"
          v-for="(key, index) in getKeys(d)"
          :key="key + index"
          @click="goToOrder(d)"
        >
          <div
            class="w-full py-4 pl-4 bg-primary-light"
            v-if="Array.isArray(d[key])"
          >
            <span v-for="(item, index) in d[key]" :key="index">
              {{ item.name }}<span v-if="index < d[key].length - 1">,</span>
            </span>
          </div>
          <div
            class="w-full py-4 pl-4 bg-primary-light"
            :class="{
              'rounded-l': index === 0,
              'rounded-r': Object.keys(headers).length - 1 === index,
            }"
            v-else
          >
            <span class="inline-block w-10/12">{{ d[key] ? d[key] : "" }}</span>
            <i
              v-if="Object.keys(headers).length - 1 === index"
              class="p-1 mt-1 mr-1 arrow right"
            />
          </div>
        </td>
      </template>
    </filter-and-sort-box>
    <div class="py-2">
      <div class="float-right">
        <i
          class="p-1 mx-1 mt-1 cursor-pointer arrow left"
          @click="setPage(page - 1)"
        />
        <span class="select-none">Page {{ page }}</span>
        <i
          class="p-1 mx-1 mt-1 cursor-pointer arrow right"
          @click="setPage(page + 1)"
        />
      </div>
    </div>
    <modal v-if="isQuickPrintModalOpen" @close="isQuickPrintModalOpen = false">
      <template slot="header">
        <img src="../assets/images/printer.svg" alt="printer" />
      </template>
      <template slot="body">
        <h4>Scan a barcode to quickprint</h4>
        <wrapper>
          <g-input
            ref="identifier"
            name="Code"
            v-model="externalIdentifier"
            label-placeholder="Code"
            @keyup.native.enter="sendQuickPrint"
            autocomplete="off"
          ></g-input>
        </wrapper>
      </template>
      <template slot="buttons">
        <g-button
          class="flex items-center m-auto mt-4 primary"
          @click="sendQuickPrint"
        >
          <svg
            v-if="!isPrinting"
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span
            v-if="isPrinting"
            class="w-3 h-3 mr-2 border-4 border-solid small-spinner-animation"
          ></span>

          Print
        </g-button>
      </template>
    </modal>
    <modal
      v-if="errorMessage && errorMessage.length"
      type="warning"
      @close="errorMessage = null"
    >
      <template slot="body">
        <h2>Error</h2>
        <p>{{ errorMessage }}</p>
      </template>
    </modal>
  </view-wrapper>
</template>

<script>
import FilterAndSortBox from "../components/FilterAndSortBox";
import GButton from "../components/forms/Button.vue";
import GInput from "../components/forms/Input.vue";
import Modal from "../components/Modal.vue";
import { sendToPrinterService } from "../utils/printerHelpers";

import { formatDate } from "../utils/dateHelpers";
import FeatureZone from "../components/FeatureZone.vue";

export default {
  components: {
    FilterAndSortBox,
    GButton,
    GInput,
    Modal,
    FeatureZone,
  },
  data() {
    return {
      orders: [],
      headers: {
        name: "Order number",
        status: "Order status",
        recieved: "Recieved",
      },
      page: 1,
      pageSize: 10,
      loading: false,
      loadingText: "Getting orders",
      searchQuery: "",
      isQuickPrintModalOpen: false,
      externalIdentifier: "",
      isPrinting: false,
      printQueues: null,
      errorMessage: null,
    };
  },
  computed: {
    wrapperName() {
      if (this.$route.params.id) {
        return "Select order";
      } else {
        return "My orders";
      }
    },
  },
  methods: {
    goToOrder(order) {
      if (this.$route.params.id) {
        this.$router.push(
          `/print-labels/${this.$route.params.id}/order/${order.id}`
        );
        return;
      }
      this.$router.push("print-order/" + order.id);
    },
    getKeys(order) {
      return Object.keys(order).filter(key => {
        return key !== "id";
      });
    },
    async setPage(newPage) {
      let val = this.page;
      if (newPage < 1) val = 1;
      else val = newPage;

      if (val === this.page) return;
      this.page = val;

      await this.getOrders();
    },
    async getOrders() {
      this.loading = true;
      this.loadingText = "Getting orders";
      let response;
      if (this.$route.params.id) {
        response = await this.$store.$api.get(
          `/api/labels/${this.$route.params.id}/orders?take=${
            this.pageSize
          }&offset=${this.pageSize * (this.page - 1)}&searchQuery=${
            this.searchQuery
          }`
        );
      } else {
        response = await this.$store.$api.get(
          `/api/orders/active?take=${this.pageSize}&offset=${this.pageSize *
            (this.page - 1)}&searchQuery=${this.searchQuery}`
        );
      }

      this.orders = response.data.data.map(o => {
        return {
          id: o.id,
          name: o.orderReference,
          status: o.status,
          recieved: formatDate(o.received),
        };
      });
      this.loading = false;
    },
    async getData() {
      await this.getOrders();
    },
    async onSearch(value) {
      this.searchQuery = value.trim();
      await this.getOrders();
    },
    openQuickPrintModal() {
      this.isQuickPrintModalOpen = true;
      this.externalIdentifier = "";
      this.$nextTick(() => this.$refs.identifier.$refs.input.focus());
    },
    async sendQuickPrint() {
      this.isPrinting = true;
      if (!this.printQueues) {
        this.printQueues = await this.$store.dispatch(
          "SETTINGS/GET_PRINT_QUEUES"
        );
      }

      let printFiles = [];

      try {
        var computerName = this.printQueues && this.printQueues.computerName;

        let response;

        if (this.$route.params.id) {
          response = await this.$store.$api.post("/api/printjobs/quickprint", {
            externalIdentifier: this.externalIdentifier,
            labelId: this.$route.params["id"],
            computerName: computerName,
            quantity: 1,
            readLatestOrder: true,
          });

          printFiles.push(response.data.data);
        } else {
          response = await this.$store.$api.post(
            "/api/printjobs/quickprint/order",
            {
              externalIdentifier: this.externalIdentifier,
              computerName: computerName,
            }
          );

          printFiles = response.data.data;

          const completeResponse = await this.$store.$api.post(
            `/api/orders/${response.data.orderId}/completed`
          );

          this.errorMessage =
            completeResponse &&
            completeResponse.data &&
            completeResponse.data.errorReason;
        }

        const printerHelperUri = this.$store.getters[
          "SETTINGS/PRINTER_HELPER_URI"
        ];

        for (const printFile of printFiles) {
          try {
            await sendToPrinterService(printerHelperUri, {
              targetAddress: printFile.targetIpAddress,
              port: printFile.targetPort,
              targetPrintQueue: printFile.targetPrintQueue,
              printerFileUrl: printFile.printFileName,
              useLocalPrinterDriver: printFile.useLocalPrinterDriver,
              pdfAsBase64String: printFile.pdfAsBase64String,
              orientation: printFile.orientation,
              scale: printFile.scale,
              side: printFile.side,
              paperSize: printFile.paperSize,
              customPaperSize: printFile.customPaperSize,
            });

            await this.$store.dispatch(
              "PRINTJOBS/UPDATE_PRINTJOBENTRY_STATUS",
              {
                printJobEntryId: printFile.printJobEntryId,
                status: "Success",
              }
            );
          } catch (error) {
            const message = "Could not print using printerhelper";
            this.errorMessage = message;

            await this.$store.dispatch(
              "PRINTJOBS/UPDATE_PRINTJOBENTRY_STATUS",
              {
                printJobEntryId: printFile.printJobEntryId,
                status: "Error",
                message: message,
              }
            );
          }
        }
      } catch (error) {
        let message;

        if (error.response) {
          if (error.response.data.Message) {
            message = error.response.data.Message;
          } else {
            message = error.response.data;
          }
        } else if (error.message) message = error.message;

        this.errorMessage = message;
      }

      this.externalIdentifier = "";
      this.isPrinting = false;
    },
  },
  async mounted() {
    await this.getData();
  },
};
</script>
<style scoped>
.small-spinner-animation {
  border-color: rgba(255, 255, 255, 0.25);
  border-top-color: rgba(255, 255, 255, 1);
  border-radius: 50%;
  animation: smallSpinnerRotation 0.6s infinite linear;
}

@keyframes smallSpinnerRotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}
</style>
